import type { ZodSchema } from 'zod'

function zodValidator<T>(schema: ZodSchema<T>) {
  return ({ value }: { value: T }) => {
    const result = schema.safeParse(value)
    return result.success ? undefined : result.error.issues[0].message
  }
}

/**
 * Creates validators that run on change and blur
 * We validate on change to keep button state updated, but only show errors after blur
 */
export function createFieldValidators<T>(schema: ZodSchema<T>) {
  const validator = zodValidator(schema)
  return {
    onChange: validator,
    onBlur: validator,
  }
}

/**
 * Creates a custom validator function
 */
export function createCustomValidator<T>(validatorFn: (value: T) => string | undefined) {
  return {
    onBlur: ({ value }: { value: T }) => validatorFn(value),
  }
}

/**
 * Scrolls the form field with the given `name` attribute into view and focuses
 * it (after the scroll settles) when it is a text input.
 */
export function scrollToFormField(fieldName: string): void {
  const element = document.querySelector(`[name="${fieldName}"]`)
  if (!element) return
  element.scrollIntoView({ behavior: 'smooth', block: 'center' })
  if (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement) {
    setTimeout(() => element.focus(), 300)
  }
}

type FieldMetaMap = Record<string, { errors?: unknown[] } | undefined>

/** The names a field may carry on the page, from its own path up to the field it belongs to. */
export function fieldElementCandidates(fieldName: string): string[] {
  const candidates = [fieldName]
  let current = fieldName
  while (true) {
    const parent = current.replace(/(\.[^.[\]]+|\[\d+\])$/, '')
    if (parent === current || !parent) break
    candidates.push(parent)
    current = parent
  }
  return candidates
}

/** The fields that currently have an error. */
export function invalidFields(fieldMeta: FieldMetaMap): string[] {
  return Object.entries(fieldMeta)
    .filter(([, meta]) => meta?.errors?.some(Boolean))
    .map(([name]) => name)
}

/** Errors no mounted validator can check again: the field is gone or validates nothing. */
export function staleErrorFields(
  fieldMeta: FieldMetaMap,
  validatorsOf: (field: string) => unknown | null,
): string[] {
  return invalidFields(fieldMeta).filter((field) => !validatorsOf(field))
}

interface SubmittableForm {
  state: { fieldMeta: FieldMetaMap }
  fieldInfo: Record<
    string,
    { instance?: { options?: { validators?: unknown } } | null } | undefined
  >
  setFieldMeta: (field: never, updater: <M extends object>(meta: M) => M) => void
  validateAllFields: (cause: 'submit') => Promise<unknown>
  handleSubmit: () => unknown
}

/**
 * Submits after dropping errors that nothing can re-check any more and validating the rest
 * again. TanStack Form refuses a submit outright while any old error is left, so a stale
 * one would otherwise block saving without a way out.
 */
export async function submitForm(form: SubmittableForm): Promise<void> {
  const stale = staleErrorFields(
    form.state.fieldMeta,
    (field) => form.fieldInfo[field]?.instance?.options?.validators ?? null,
  )
  for (const field of stale) {
    form.setFieldMeta(field as never, (meta) => ({ ...meta, errorMap: {}, errorSourceMap: {} }))
  }
  await form.validateAllFields('submit')
  await form.handleSubmit()
}

function elementForField(fieldName: string): Element | null {
  for (const name of fieldElementCandidates(fieldName)) {
    const element =
      document.querySelector(`[name="${CSS.escape(name)}"]`) ??
      document.getElementById(name) ??
      document.querySelector(`[data-field="${CSS.escape(name)}"]`)
    if (element) return element
  }
  return null
}

/** Scrolls to the first field with an error on the page and focuses it; false if none shows. */
export function focusFirstInvalidField(fieldMeta: FieldMetaMap): boolean {
  const elements = invalidFields(fieldMeta)
    .map(elementForField)
    .filter((element): element is Element => element !== null)
  if (elements.length === 0) return false
  const [first] = elements.sort((a, b) =>
    a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1,
  )
  const folded = first.closest('details')
  if (folded) folded.open = true
  first.scrollIntoView({ behavior: 'smooth', block: 'center' })
  if (first instanceof HTMLElement) setTimeout(() => first.focus({ preventScroll: true }), 300)
  return true
}

/**
 * Normalizes a URL input by prepending https:// if no protocol is present.
 * Returns empty string unchanged (field is optional).
 */
export function normalizeUrl(input: string): string {
  const trimmed = input.trim()
  if (!trimmed) return ''
  if (trimmed.startsWith('https://') || trimmed.startsWith('http://')) return trimmed
  return `https://${trimmed}`
}
